import * as React from "react";
// lodash
import _ from "lodash";
// redux
import {connect, Dispatch} from "react-redux";
import {ITranslatedComponent, ITypefamilyMap} from "../../../redux/reducers/generalReducer";
import SaveConfirmDialog from "./SaveConfirmDialog";
// translations
import {translate} from "react-i18next";
// dtos, etc...
import {
    Link,
    MenuDto,
    OptionBasicDtoOptionType,
    ResourceListOfRoleDto,
    ResourceListOfWorkflowDto,
    RoleDto,
    TypefamilyDto,
    WorkflowDto,
    WorkflowDtoPartition,
    WorkflowDtoType,
    WorkflowStatusDto,
    WorkflowStatusDtoType
} from "../../../services/Client";
import HATEOASClient from "../../../services/HATEOASClient";
// componenents
import {Checkbox, Input, Segment} from "semantic-ui-react";
import SelectField from "../../../components/SelectField";
import Button from "../../../components/Button";
import {sortOptionTypes} from "../../../components/Table/dataHandler/Utils";
import {Workflow} from "./Workflow";
import {WorkflowAddDialog} from "./WorkflowAddDialog";
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import RoleSelection from "../AccessTab/RoleSelection";
import {getDtoArray} from "../AdminUtils";
import GeneralActions from "../../../redux/actions/GeneralActions";
import {selectBaseLinks, selectClient, selectTypefamilyMap, selectRoles} from "../../../redux/selectors";
import {compose} from "redux";
import {IReduxState} from "../../../redux/IReduxState";

interface IMapStateToProps {
    client: HATEOASClient;
    baseLinks?: MenuDto;
    typefamilyMap?: ITypefamilyMap;
    roles?: ResourceListOfRoleDto;
}

interface IMapDispatchToProps {
    addLoadingMessage: (message: string) => void;
    removeLoadingMessage: (message: string) => void;
}

interface IWorkflowTabProps extends IMapStateToProps, IMapDispatchToProps, ITranslatedComponent {
}

interface IWorkflowTabState {
    showCreateDialog: boolean;
    loadingWorkflows: boolean;
    editMode: boolean;
    isShowUpdateConfirmDialog: boolean;
    selectedWorkflow?: WorkflowDto;
    workflowResourceList?: ResourceListOfWorkflowDto;
    workflowList: WorkflowDto[];
    typefamilies: String[];
}

class WorkflowTab extends React.Component<IWorkflowTabProps, IWorkflowTabState> {
    private workflowTypes: WorkflowDtoType[];
    private optionTypes: OptionBasicDtoOptionType[];
    private readonly dummyAllEditorsRoleObjectId: number = 0;
    private dummyAllEditorsRole = {
        name: this.props.t('WORKFLOW:ALL_EDITORS'),
        objectId: this.dummyAllEditorsRoleObjectId,
        versionId: -9999
    };

    constructor(props: IWorkflowTabProps) {
        super(props);

        this.workflowTypes = Object.keys(WorkflowDtoType).map((key: string) => key as WorkflowDtoType);
        this.optionTypes = Object.keys(OptionBasicDtoOptionType).map((key: string) => key as OptionBasicDtoOptionType).sort(sortOptionTypes);

        this.state = {
            showCreateDialog: false,
            loadingWorkflows: false,
            editMode: false,
            isShowUpdateConfirmDialog: false,
            workflowList: [],
            typefamilies: []
        };
    }

    componentDidMount() {
        this.loadWorkflows();
    }

    /**
     * Method to load all workflows (to be able to select them).
     */
    loadWorkflows(): void {
        const {
            baseLinks,
            client,
            typefamilyMap
        } = this.props;

        if (baseLinks) {
            if (client.GET_workflows(baseLinks).canExecute()) {
                this.setState({loadingWorkflows: true});

                client.GET_workflows(baseLinks).execute()
                    .then((workflowResource: ResourceListOfWorkflowDto) => {

                        // need to use a set here because of duplicates (typefamilyMap contains all typefamilies for each model year)
                        const tfCodeNameSet: Set<String> = new Set();
                        // need a map here in order to add all the typefamilies based on sort sequence.
                        // in some years we do not have all typefamilies and we need to make sure that we
                        // use sort sequence when displaying in dropdown
                        const tfCodeNameMap: Map<Number, string> = new Map<Number, string>();

                        if (typefamilyMap) {
                            Object.keys(typefamilyMap).forEach((key: string) => {
                                const elements: TypefamilyDto[] = typefamilyMap &&
                                    typefamilyMap[key] ?
                                    typefamilyMap[key].elements as TypefamilyDto[] : [];
                                elements.forEach((item: TypefamilyDto) => {
                                    tfCodeNameMap[item.sortSequence!] = item.typefamily;
                                });
                            });
                            Object.keys(tfCodeNameMap).map(key => {
                                tfCodeNameSet.add(tfCodeNameMap[key]);
                            });
                        }
                        const selectedWfId: number = this.state.selectedWorkflow ? this.state.selectedWorkflow.objectId : 0;

                        this.setReceviedSelectedWorkflow(selectedWfId !== 0 && workflowResource.elements
                            ? workflowResource.elements.find((wf: WorkflowDto) => wf.objectId === selectedWfId)
                            : undefined);

                        this.setState({
                            loadingWorkflows: false,
                            editMode: false,
                            workflowResourceList: workflowResource,
                            workflowList: workflowResource.elements ? workflowResource.elements : [],
                            typefamilies: Array.from(tfCodeNameSet)
                        });
                    })
                    .catch((reason: any) => {
                        renderErrorToast({
                            message: "ERRORS:GET_WORFKLOWS",
                            details: reason,
                            timestamp: Date.now()
                        });
                        this.setState({loadingWorkflows: false});
                    });
            } else {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:NOT_ALLOWED",
                    timestamp: Date.now()
                });
            }
        }
    }

    /**
     * Checks if new workflows can be created
     * @return true, if a new workflow can be created
     */
    canAddNewWorkflow(): boolean {
        return this.state.workflowResourceList ?
            this.props.client.POST_workflow(this.state.workflowResourceList).canExecute() : false;

    }

    /**
     * Method to create a new workflow.
     *
     * @param workflowType - the type of the workflow
     * @param optionTypes - the option types the workflow is valid for
     * @param typefamilies - the typefamilies the workflow is valid for
     */
    createWorkflow(workflowType: WorkflowDtoType, optionTypes?: OptionBasicDtoOptionType[], typefamilies?: String[]): void {
        const {roles} = this.props;
        if (roles && roles.elements) {
            let partition: WorkflowDtoPartition;
            switch (workflowType) {
                case WorkflowDtoType.OPTION_BASIC:
                    partition = WorkflowDtoPartition.BASIC_TEXTS;
                    break;
                case WorkflowDtoType.OPTION_PARTITION:
                    partition = WorkflowDtoPartition.INTERNAL_TEXTS;
                    break;
                case WorkflowDtoType.KUMA:
                    partition = WorkflowDtoPartition.KUMA_TEXTS;
                    break;
                default:
                    throw Error("Unknown workflow type.");
            }

            // just take any role as default, does not matter now
            let initialRole: RoleDto = roles.elements[0];
            let optionTypesString;
            let typefamiliesString;
            if (workflowType !== WorkflowDtoType.KUMA) {
                optionTypesString = _.join(optionTypes, ',');
                typefamiliesString = _.join(typefamilies, ',');
            }

            let newWorkflow: WorkflowDto = {
                objectId: 0,
                versionId: 0,
                type: workflowType,
                name: "NO_NAME",
                partition: partition,
                optionTypes: optionTypesString,
                typefamilies: typefamiliesString,
                resetPointRejection: false,
            };

            // we need a dummy workflow to assign it to the initial workflow steps we have to create
            // does not work with the "normal" workflow because of circular dependencies
            let dummyWorkflowForStatus: WorkflowDto = {
                objectId: 0,
                versionId: 0,
                type: workflowType,
                name: "",
                partition: partition
            };

            // add two initial workflow steps
            const initialStatus = [{
                objectId: 0,
                versionId: 0,
                sequenceNumber: 0,
                name: workflowType === WorkflowDtoType.KUMA ? "Funnel" : "Maintenance",
                role: initialRole,
                type: WorkflowStatusDtoType.MAINTENANCE,
                resetPoint: true,
                workflow: dummyWorkflowForStatus
            },
            {
                objectId: 0,
                versionId: 0,
                sequenceNumber: 1,
                name: "Approved",
                role: initialRole,
                type: WorkflowStatusDtoType.APPROVED,
                resetPoint: false,
                workflow: dummyWorkflowForStatus
            }];

            newWorkflow.status = initialStatus;
            if(workflowType === WorkflowDtoType.KUMA) {
                newWorkflow.resetRoles =this.getKumaResetRoles();
            } else {
                newWorkflow.resetRoles = [initialRole];
            }

            this.saveWorkflow(newWorkflow);
        }
    }

    /**
     * Method to save a workflow
     *
     * @param workflow - The workflow object to save
     */
    saveWorkflow(workflow: WorkflowDto): void {
        if (this.state.workflowResourceList && this.props.client.POST_workflow(this.state.workflowResourceList).canExecute()) {
            const loadingMsg = this.props.t("SAVE_WORKFLOW");
            this.props.addLoadingMessage(loadingMsg);

            this.props.client.POST_workflow(this.state.workflowResourceList).execute(workflow)
                .then((link: Link) => {
                    return this.props.client.GET_workflowSelf(link).execute();
                })
                .then((newWorkflow: WorkflowDto) => {
                    this.props.removeLoadingMessage(loadingMsg);

                    this.setReceviedSelectedWorkflow(newWorkflow);

                    this.setState({
                        showCreateDialog: false
                    });

                    this.loadWorkflows();
                })
                .catch((reason: any) => {
                    this.props.removeLoadingMessage(loadingMsg);
                    window.scrollTo(0, 0);
                    this.loadWorkflows();
                    renderErrorToast({
                        message: "ERRORS:SAVE_WORKFLOW",
                        details: reason,
                        timestamp: Date.now()
                    });
                });
        } else {
            window.scrollTo(0, 0);
            renderErrorToast({
                message: "ERRORS:NOT_ALLOWED",
                timestamp: Date.now()
            });
        }
    }

    /**
     * Checks if a workflow can be updated
     * @return true, if updateable
     */
    canUpdateWorkflow(): boolean {
        return this.state.selectedWorkflow ?
            this.props.client.UPDATE_workflow(this.state.selectedWorkflow).canExecute() : false;
    }

    /**
     * Method to update a workflow
     */
    updateWorkflow(workflow?: WorkflowDto): void {
        const loadingMsg = this.props.t("LOAD_UPDATE_WORKFLOW");

        if (workflow) {
            if (workflow.resetRoles && workflow.resetRoles.find(role => role.objectId === this.dummyAllEditorsRoleObjectId) !== undefined) {
                const allEditorsRoles: RoleDto[] = [];
                if (workflow.status) {
                    workflow.status.map(status => {
                        if (allEditorsRoles.find(role => role.objectId === status.role.objectId) === undefined) {
                            allEditorsRoles.push(status.role);
                        }
                    });
                }

                workflow.resetRoles = [
                    ...workflow.resetRoles.filter(role => role.objectId !== this.dummyAllEditorsRoleObjectId),
                    ...allEditorsRoles
                ];
            }
            if (this.props.client.UPDATE_workflow(workflow).canExecute()) {
                this.props.addLoadingMessage(loadingMsg);

                this.props.client.UPDATE_workflow(workflow).execute(workflow)
                    .then((link: Link) => {
                        this.props.removeLoadingMessage(loadingMsg);
                        this.loadWorkflows();
                    })
                    .catch((reason: any) => {
                        this.props.removeLoadingMessage(loadingMsg);
                        window.scrollTo(0, 0);
                        this.loadWorkflows();
                        renderErrorToast({
                            message: "ERRORS:UPDATE_WORKFLOW",
                            details: reason,
                            timestamp: Date.now()
                        });
                    });
            } else {
                this.props.removeLoadingMessage(loadingMsg);
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:NOT_ALLOWED",
                    timestamp: Date.now()
                });
            }
        }
    }

    /**
     * Called when the typefamily/optionTypes values are changed for the selected workflow
     */
    private setMultiSelectValue(values: string[] | OptionBasicDtoOptionType[], key: string) {
        if (this.state.selectedWorkflow) {
            const updatedWorkflow = _.cloneDeep(this.state.selectedWorkflow);
            updatedWorkflow[key] = _.join(values, ',');

            this.setState({selectedWorkflow: updatedWorkflow});
        }
    }

    /**
     * Called when the reset roles are changed for the selected workflow
     */
    private setSelectedResetRoles = (values: RoleDto[]) => {
        if (this.state.selectedWorkflow) {
            const isAllEditorsSelected = values.find(role => role.objectId === this.dummyAllEditorsRoleObjectId) !== undefined;
            const updatedWorkflow = _.cloneDeep(this.state.selectedWorkflow);

            if (isAllEditorsSelected) {
                updatedWorkflow.resetRoles = values.filter(role => updatedWorkflow.status && updatedWorkflow.status.length > 0
                    ? updatedWorkflow.status.find(status => status.role.objectId === role.objectId) === undefined
                    : true);
            } else {
                updatedWorkflow.resetRoles = values;
            }

            this.setState({
                selectedWorkflow: updatedWorkflow,
            });
        }
    }

    /**
     * @returns the dialog for confirming the a workflow update
     */
    private showUpdateConfirmDialog() {
        if (this.state.selectedWorkflow && this.state.isShowUpdateConfirmDialog) {
            return (
                <SaveConfirmDialog
                    title={this.props.t("SAVE_WORKFLOW_TITLE")}
                    children={this.props.t("SAVE_WORKFLOW_QUESTION")}
                    onSave={() => {
                        this.setState({isShowUpdateConfirmDialog: false});
                        this.updateWorkflow(this.state.selectedWorkflow);
                    }}
                    onCancel={() =>
                        this.setState({
                            isShowUpdateConfirmDialog: false
                        })
                    }
                    onExit={() => {
                        this.setState({
                            isShowUpdateConfirmDialog: false,
                            editMode: false
                        });
                        this.loadWorkflows();
                    }
                    }
                />
            );
        }

        return null;
    }

    /**
     * Checks if a workflow can be deleted
     * @return true, if deletable
     */
    canDeleteWorkflow(): boolean {
        return this.state.selectedWorkflow ?
            this.props.client.DELETE_workflow(this.state.selectedWorkflow).canExecute() : false;
    }

    /**
     * Method to delete a selected workflow
     */
    deleteSelectedWorkflow(): void {
        if (this.state.selectedWorkflow) {
            const loadingMsg = this.props.t("DELETE_WORKFLOW");
            this.props.addLoadingMessage(loadingMsg);
            this.props.client.DELETE_workflow(this.state.selectedWorkflow).execute(this.state.selectedWorkflow)
                .then(() => {
                    this.props.removeLoadingMessage(loadingMsg);
                    this.loadWorkflows();
                })
                .catch((reason: any) => {
                    this.props.removeLoadingMessage(loadingMsg);
                    window.scrollTo(0, 0);
                    this.loadWorkflows();
                    renderErrorToast({
                        message: "ERRORS:DELETE_WORKFLOW",
                        details: reason,
                        timestamp: Date.now()
                    });
                });
        }
    }

    private setDummyAllEditorsRole = (statuses: WorkflowStatusDto[] | undefined) => {
        let names: Set<string> = new Set<string>();
        if (statuses !== undefined) {
            statuses.map(status => status.type !== WorkflowStatusDtoType.APPROVED && names.add(status.role.name));
        }

        this.dummyAllEditorsRole.name = `${this.props.t('WORKFLOW:ALL_EDITORS')} - (${Array.from(names).join(', ')})`;
    }

    setSelectedWorkflow = (workflow: WorkflowDto) => {
        this.setState({selectedWorkflow: workflow});
    }

    setReceviedSelectedWorkflow = (workflow: WorkflowDto | undefined) => {
        if (workflow && workflow.resetRoles && workflow.status) {
            let hasAllEditorsSelected = true;

            workflow.status.map(status => {
                if (status.type !== WorkflowStatusDtoType.APPROVED
                    // @ts-ignore condition checked before
                    && workflow.resetRoles.find(role => role.objectId === status.role.objectId && status.type) === undefined) {
                    hasAllEditorsSelected = false;
                }
            });

            if (hasAllEditorsSelected) {
                workflow.resetRoles = [
                    ...workflow.resetRoles.filter(role =>
                        // @ts-ignore condition checked before
                        workflow.status.find(status => status.role.objectId === role.objectId && status.type !== WorkflowStatusDtoType.APPROVED) === undefined),
                    this.dummyAllEditorsRole,
                ];
            }
        }

        this.setState({selectedWorkflow: workflow});
    }

    getKumaResetRoles = () => this.props.roles &&
        this.props.roles.elements &&
        this.props.roles.elements.filter(
            (p) => p.privileges && p.privileges.some(q => q.identifier === 'KUMA_WRITE'
                || q.identifier === 'KUMA_MANAGER')
        )

    render() {
        const {
            typefamilies,
            workflowList,
            selectedWorkflow,
            editMode,
            loadingWorkflows,
            showCreateDialog,
        } = this.state;

        const {
            t,
            roles
        } = this.props;

        this.setDummyAllEditorsRole(selectedWorkflow && selectedWorkflow.status);
        const resetRolesDataSource = [this.dummyAllEditorsRole, ...getDtoArray<RoleDto>(roles)];
        let resetRoles: RoleDto[];
        let allEditorsSelected = false;

        if (selectedWorkflow !== undefined) {
            if (selectedWorkflow.resetRoles
                && selectedWorkflow.resetRoles.length > 0
                && selectedWorkflow.resetRoles.find(role => role.objectId === this.dummyAllEditorsRoleObjectId) !== undefined
            ) {
                resetRoles = resetRolesDataSource.filter(role => selectedWorkflow.status && selectedWorkflow.status.length > 0
                    ? selectedWorkflow.status.find(status => status.role.objectId === role.objectId) === undefined
                    : true);
            } else {
                resetRoles = resetRolesDataSource;
            }
        } else {
            resetRoles = [];
        }

        return (
            <Segment loading={loadingWorkflows}>
                {
                    this.showUpdateConfirmDialog()
                }

                <Button
                    label={t("GLOBAL:RELOAD")}
                    icon={"refresh"}
                    labelPosition={"after"}
                    floated={"right"}
                    onClick={() => this.loadWorkflows()}
                />

                <WorkflowAddDialog
                    open={showCreateDialog}
                    workflowTypes={this.workflowTypes}
                    optionTypes={this.optionTypes}
                    typefamilies={typefamilies}
                    requestClose={() => this.setState({showCreateDialog: false})}
                    onCreateNewWorkflow={(selectedWorkflowType: WorkflowDtoType, selectedOptionTypes?: OptionBasicDtoOptionType[], selectedTypefamilies?: String[]) => {
                        this.setState({showCreateDialog: false});
                        this.createWorkflow(selectedWorkflowType, selectedOptionTypes, selectedTypefamilies);
                    }
                    }
                />

                {this.canAddNewWorkflow() &&
                    <Button
                        disabled={editMode}
                        label={t("NEW_WORKFLOW")}
                        icon={"add"}
                        onClick={() => this.setState({showCreateDialog: true})}
                    />
                }

                {this.canDeleteWorkflow() &&
                    <Button
                        disabled={editMode || (selectedWorkflow && selectedWorkflow.type === WorkflowDtoType.KUMA)}
                        label={t("DELETE_WORKFLOW")}
                        icon={"delete"}
                        onClick={() => this.deleteSelectedWorkflow()}
                    />
                }

                {this.canUpdateWorkflow() &&
                    <Button
                        icon={editMode ? "close" : "pencil"}
                        label={editMode ? t("STOP_EDIT_WORKFLOW") : t("EDIT_WORKFLOW")}
                        onClick={() => editMode
                            ? this.setState({isShowUpdateConfirmDialog: true})
                            : this.setState({editMode: !editMode})
                        }
                    />
                }

                <div className={"margin-top--10px margin-left--5px"}>
                    <span className={"WorkflowHeadline"}>
                        {t("SELECT_WORKFLOW")}
                    </span>
                    <SelectField
                        value={workflowList.find((wf: WorkflowDto) => selectedWorkflow ? wf.objectId === selectedWorkflow.objectId : false)}
                        dataSource={workflowList}
                        searchable={true}
                        readonly={editMode}
                        rendererKey={(workflow: WorkflowDto) => workflow.name}
                        onChange={this.setReceviedSelectedWorkflow}
                    />
                </div>

                {selectedWorkflow &&
                    <>
                        < div className='workflow-divider' />

                        {selectedWorkflow.active && selectedWorkflow.active !== "TRUE" &&
                            <div style={{height: 50, background: "#db2828", padding: 15, marginLeft: 5, color: 'white'}}>
                                {t("WORKFLOW:WORKFLOW_DELETED")}
                            </div>
                        }

                        <div className={"margin-top--10px margin-left--5px"}>
                            <span className={"OptionTypeHeadline"}>
                                {t("WORKFLOW:WORKFLOW_STATE_NAME")}
                            </span>
                            <Input
                                className='workflow-name-input'
                                fluid={true}
                                disabled={!editMode}
                                value={selectedWorkflow.name}
                                onChange={(e: any, d: any) => this.setState({
                                    selectedWorkflow: {
                                        ...selectedWorkflow,
                                        name: d.value
                                    }
                                })}
                            />
                        </div>
                        {selectedWorkflow.type !== WorkflowDtoType.KUMA && <>
                        <div className={"margin-top--10px margin-left--5px"}>
                            <span className={"OptionTypeHeadline"}>
                                {t("OPTION_TYPES")}
                            </span>
                            <SelectField
                                value={selectedWorkflow.optionTypes ?
                                    selectedWorkflow.optionTypes.split(',') as OptionBasicDtoOptionType[] : []}
                                dataSource={this.optionTypes}
                                searchable={true}
                                readonly={!editMode}
                                multi={true}
                                rendererKey={(value: OptionBasicDtoOptionType) => t("GLOBAL:" + value) + " (" + value + ')'}
                                onChange={(values: any[]) => this.setMultiSelectValue(
                                    (values.map((v) => v.selectedValue) as OptionBasicDtoOptionType[]),
                                    'optionTypes'
                                )
                                }
                            />
                        </div>

                            <div className={"margin-top--10px margin-left--5px"}>
                                <span className={"FamilyTypeHeadline"}>
                                    {t("TYPE_FAMILIES")}
                                </span>
                                <SelectField
                                    value={selectedWorkflow.typefamilies ?
                                        selectedWorkflow.typefamilies.split(',') : []}
                                    dataSource={typefamilies}
                                    searchable={true}
                                    readonly={!editMode}
                                    multi={true}
                                    rendererKey={(value: string) => {
                                        if (value === 'M') {
                                            return t("GLOBAL:M_TYPEFAMILY");
                                        }
                                        return t("GLOBAL:" + value);
                                    }}
                                    onChange={(values: any[]) =>
                                        this.setMultiSelectValue((values.map((v) => v.selectedValue) as string[]), 'typefamilies')
                                    }
                                />
                            </div> </>}
                        {(selectedWorkflow.type === WorkflowDtoType.OPTION_PARTITION || selectedWorkflow.type === WorkflowDtoType.KUMA) &&
                            <React.Fragment>
                                <div className={"margin-top--10px margin-left--5px"}>
                                    <span className={"FamilyTypeHeadline"}>
                                        {t("WORKFLOW:RESET_ROLES")}
                                    </span>
                                    <RoleSelection
                                        readOnly={!editMode}
                                        roles={selectedWorkflow.type === WorkflowDtoType.KUMA ? this.getKumaResetRoles() || []: resetRoles}
                                        searchable={true}
                                        values={selectedWorkflow.resetRoles}
                                        onChange={this.setSelectedResetRoles}
                                    />
                                </div>
                                {selectedWorkflow.type !== WorkflowDtoType.KUMA && <div className={"margin-top--10px margin-left--5px display--flex align-items--center"}>
                                    <Checkbox
                                        className='workflow-reject-point-checkbox'
                                        checked={selectedWorkflow.resetPointRejection}
                                        disabled={!editMode}
                                        onChange={() => this.setState({
                                            selectedWorkflow: {
                                                ...selectedWorkflow,
                                                resetPointRejection: !selectedWorkflow.resetPointRejection
                                            }
                                        })}
                                    />
                                    <span className={"FamilyTypeHeadline"}>
                                        {t("WORKFLOW:REJECT_TO_RESET_POINT")}
                                    </span>
                                </div>}
                            </React.Fragment>
                        }

                        < div className='workflow-divider' />

                        {roles && roles.elements &&
                            <Workflow
                                allEditorsSelected={allEditorsSelected}
                                roles={selectedWorkflow.type === WorkflowDtoType.KUMA ? this.getKumaResetRoles() || []: roles.elements}
                                workflow={selectedWorkflow}
                                edit={editMode}
                                setWorkflow={this.setSelectedWorkflow}
                            />
                        }
                    </>
                }
            </Segment>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    baseLinks: selectBaseLinks(state),
    client: selectClient(state),
    typefamilyMap: selectTypefamilyMap(state),
    roles: selectRoles(state)
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: IWorkflowTabProps): IMapDispatchToProps => ({
    addLoadingMessage: (message: string) => dispatch(GeneralActions.AddLoadingMessage(ownProps.t(message))),
    removeLoadingMessage: (message: string) => dispatch(GeneralActions.RemoveLoadingMessage(ownProps.t(message))),
});

export default compose(
    translate(["WORKFLOW", "ERRORS"]),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(WorkflowTab);



// WEBPACK FOOTER //
// ./src/views/AdminView/WorkflowTab/WorkflowTab.tsx